/**
 * Hook för OdometerReport
 * Hanterar state och logik för mätarställningsrapport
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { format, subDays } from 'date-fns';
import i18n from '@/i18n/config';
import { useAuthStore } from '@/store/authStore';
import api from '@/services/api/client';
import toast from 'react-hot-toast';
import {
  Vehicle,
  OdometerEntry,
  ReportSummary,
  generateCsvContent,
  downloadCsv,
} from '@/types/odometerReport';

export function useOdometerReport() {
  const { user } = useAuthStore();

  // Filter state
  const [selectedDate, setSelectedDate] = useState<string>(
    format(subDays(new Date(), 1), 'yyyy-MM-dd')
  );
  const [selectedOrgId, setSelectedOrgId] = useState<string>('');
  const [selectedVehicleId, setSelectedVehicleId] = useState<string>('');

  // Data state
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [entries, setEntries] = useState<OdometerEntry[]>([]);
  const [summary, setSummary] = useState<ReportSummary | null>(null);

  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingVehicles, setIsLoadingVehicles] = useState(false);

  // Vehicle dropdown state
  const [vehicleSearch, setVehicleSearch] = useState('');
  const [showVehicleDropdown, setShowVehicleDropdown] = useState(false);

  // Select organization (används av OrgTreeSelect; rensar valt fordon)
  const selectOrganization = useCallback((orgId: string) => {
    setSelectedOrgId(orgId);
    setSelectedVehicleId('');
  }, []);

  // Toggle vehicle dropdown
  const toggleVehicleDropdown = useCallback(() => {
    setShowVehicleDropdown((prev) => !prev);
  }, []);

  // Close dropdowns
  const closeDropdowns = useCallback(() => {
    setShowVehicleDropdown(false);
  }, []);

  // Select vehicle
  const selectVehicle = useCallback((vehicleId: string) => {
    setSelectedVehicleId(vehicleId);
    setShowVehicleDropdown(false);
    setVehicleSearch('');
  }, []);

  // Filtered vehicles based on search
  const filteredVehicles = useMemo(() => {
    if (!vehicleSearch.trim()) return vehicles;
    const search = vehicleSearch.toLowerCase();
    return vehicles.filter(
      (v) =>
        v.registrationNumber.toLowerCase().includes(search) ||
        (v.brand && v.brand.toLowerCase().includes(search)) ||
        (v.model && v.model.toLowerCase().includes(search))
    );
  }, [vehicles, vehicleSearch]);

  // Get selected vehicle name
  const selectedVehicleName = useMemo(() => {
    if (!selectedVehicleId) return i18n.t('copy.reports.allVehicles');
    const v = vehicles.find((v) => v.id.toString() === selectedVehicleId);
    return v
      ? `${v.registrationNumber} ${v.brand || ''} ${v.model || ''}`.trim()
      : i18n.t('copy.reports.unknown');
  }, [selectedVehicleId, vehicles, i18n.language]);

  // Load vehicles when organization changes
  useEffect(() => {
    if (!selectedOrgId) {
      setVehicles([]);
      return;
    }

    const fetchVehicles = async () => {
      try {
        setIsLoadingVehicles(true);
        const params: any = { limit: 1000, organizationId: selectedOrgId };
        const response = await api.get('/api/vehicles', { params });
        if (response.data.success) {
          const vehicleList = (response.data.data || []).map((v: any) => ({
            id: v.id || v.vehicle_id,
            registrationNumber: v.registration_number || v.registrationNumber,
            brand: v.brand,
            model: v.model,
          }));
          setVehicles(
            vehicleList.sort((a: Vehicle, b: Vehicle) =>
              a.registrationNumber.localeCompare(b.registrationNumber)
            )
          );
        }
      } catch {
        setVehicles([]);
      } finally {
        setIsLoadingVehicles(false);
      }
    };

    fetchVehicles();
  }, [selectedOrgId]);

  // Fetch report
  const fetchReport = useCallback(async () => {
    if (!selectedDate) {
      toast.error(i18n.t('copy.reports.chooseDate'));
      return;
    }

    setIsLoading(true);
    try {
      const params: any = { date: selectedDate };
      if (selectedVehicleId) {
        params.vehicleId = selectedVehicleId;
      } else if (selectedOrgId) {
        params.organizationId = selectedOrgId;
      } else if (user?.organization_id && !user?.is_global_admin) {
        params.organizationId = user.organization_id;
      }

      const response = await api.get('/api/reports/odometer', { params });

      if (response.data.success) {
        setEntries(response.data.data.entries || []);
        setSummary(response.data.data.summary || null);
      } else {
        toast.error(response.data.error || i18n.t('copy.reports.fetchReportFailed'));
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || i18n.t('copy.reports.fetchReportFailed'));
    } finally {
      setIsLoading(false);
    }
  }, [selectedDate, selectedVehicleId, selectedOrgId, user]);

  // Export to CSV
  const exportToCsv = useCallback(() => {
    if (entries.length === 0) {
      toast.error(i18n.t('copy.reports.noDataToExport'));
      return;
    }

    const csvContent = generateCsvContent(entries, summary, selectedDate);
    downloadCsv(csvContent, `${i18n.t('copy.reports.odometerFilePrefix')}_${selectedDate}.csv`);
    toast.success(i18n.t('copy.reports.reportExported'));
  }, [entries, summary, selectedDate]);

  return {
    // Data
    vehicles,
    entries,
    summary,

    // Filter state
    selectedDate,
    selectedOrgId,
    selectedVehicleId,
    selectedVehicleName,

    // UI state
    isLoading,
    isLoadingVehicles,
    vehicleSearch,
    showVehicleDropdown,
    filteredVehicles,

    // Setters
    setSelectedDate,
    setVehicleSearch,

    // Actions
    selectOrganization,
    toggleVehicleDropdown,
    closeDropdowns,
    selectVehicle,
    fetchReport,
    exportToCsv,
  };
}
